import { useMemo } from 'react';

import { Modal } from '@dbt-labs/sourdough';

import { ProjectEnvironmentSelector } from '@/components/ProjectEnvironmentSelector';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { InsightsDataWarehouseObject } from '@/hooks/query/types';
import { useHandoffEventAccount } from '@/hooks/telemetry/handoffs';
import { useProjectEnvironmentSelector } from '@/hooks/useProjectEnvironmentSelector';
import { resourceTypeToDWHHandoffType } from '@/hooks/visual-editor/types';
import { toHandoffAssetDataWarehouseType } from '@/util/handoffs';
import { insightsDWH } from '@/util/linkGeneratorsDbtCloud';

type InsightsHandoffModalProps = {
  asset: InsightsDataWarehouseObject;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

const InsightsHandoffModal = ({
  asset,
  isOpen,
  setIsOpen,
}: InsightsHandoffModalProps) => {
  const { sendHandoffEventAccount } = useHandoffEventAccount();

  const { accountId } = useDbtCloudAccount();

  const {
    selectedProjectId,
    selectedEnvironmentId,
    projectOptions,
    environmentOptions,
    isLoading,
    isLoadingEnvironments,
    selectedProjectOption,
    selectedEnvironmentOption,
    handleProjectChange,
    handleEnvironmentChange,
  } = useProjectEnvironmentSelector({ isOpen });

  const queryHandoffLink = useMemo(() => {
    if (!selectedProjectId || !selectedEnvironmentId) return '';

    return insightsDWH({
      accountId: accountId!,
      projectId: selectedProjectId,
      environmentId: selectedEnvironmentId,
      objectType: resourceTypeToDWHHandoffType[asset.type],
      objectId: asset.qualifiedName,
      warehouseType: asset.warehouseType,
    });
  }, [accountId, selectedProjectId, selectedEnvironmentId, asset]);

  const actions = [
    {
      text: 'Open',
      type: 'primary' as const,
      onClick: () => {
        if (queryHandoffLink) {
          sendHandoffEventAccount({
            handoff_to: 'insights',
            is_final_handoff_action: true,
            asset_type: toHandoffAssetDataWarehouseType(asset.type),
          });
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = queryHandoffLink;
          setIsOpen(false);
        }
      },
      buttonProps: {
        isDisabled: !selectedProjectId || !selectedEnvironmentId,
      },
    },
  ];

  return (
    <Modal
      title="Open in Insights"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      modalType="success"
      overflow="visible"
      actions={actions}
    >
      <div className="mt-2">
        <ProjectEnvironmentSelector
          projectOptions={projectOptions}
          environmentOptions={environmentOptions}
          selectedProjectOption={selectedProjectOption}
          selectedEnvironmentOption={selectedEnvironmentOption}
          isLoading={isLoading}
          isLoadingEnvironments={isLoadingEnvironments}
          onProjectChange={handleProjectChange}
          onEnvironmentChange={handleEnvironmentChange}
        />
      </div>
    </Modal>
  );
};

export default InsightsHandoffModal;
